import React, { useState } from 'react';
import {
  Wrench,
  PlusCircle,
  LayoutDashboard,
  Users,
  TrendingUp,
  Settings,
  LogOut,
  Package,
  AlertTriangle,
  ShieldCheck,
  User,
  ExternalLink,
  Smartphone,
  Check,
  Copy,
  Archive,
} from 'lucide-react';
import { AppUser, CompanyInfo, InventoryItem, ServiceTicket } from '../types';

interface HeaderProps {
  activeTab: 'dashboard' | 'history' | 'inventory' | 'reports' | 'archive';
  setActiveTab: (tab: 'dashboard' | 'history' | 'inventory' | 'reports' | 'archive') => void;
  onOpenNewTicket: () => void;
  onOpenSettings: () => void;
  onResetData: () => void;
  onRefreshData?: () => void;
  onLogout?: () => void;
  isSyncing?: boolean;
  companyInfo: CompanyInfo;
  tickets: ServiceTicket[];
  inventory?: InventoryItem[];
  currentUser?: AppUser | null;
}

export const Header: React.FC<HeaderProps> = ({
  activeTab,
  setActiveTab,
  onOpenNewTicket,
  onOpenSettings,
  onResetData,
  onLogout,
  companyInfo,
  tickets,
  inventory = [],
  currentUser,
}) => {
  const isTechnician = currentUser?.role === 'technician';
  const isLimitedTechnician = currentUser?.role === 'limited_technician';
  const isAdmin = currentUser?.role === 'admin' || (!currentUser && !isTechnician && !isLimitedTechnician);
  const activeCount = tickets.filter(
    t => t.status !== 'Hazir' && t.status !== 'TeslimEdildi' && t.status !== 'IadeEdildi' && t.status !== 'IadeEdilecek' && t.status !== 'Iptal'
  ).length;

  const readyCount = tickets.filter(t => t.status === 'Hazir').length;

  const criticalStockCount = inventory.filter(
    i => (i.quantity || 0) <= (i.minQuantity || 2)
  ).length;

  const [copiedTrackLink, setCopiedTrackLink] = useState(false);

  const handleCopyTrackingLink = () => {
    const trackingUrl = typeof window !== 'undefined' ? `${window.location.origin}/takip` : '';
    navigator.clipboard.writeText(trackingUrl);
    setCopiedTrackLink(true);
    setTimeout(() => setCopiedTrackLink(false), 2000);
  };

  return (
    <>
      <header className="bg-slate-900 text-white shadow-lg sticky top-0 z-30 border-b border-slate-800 pt-safe">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16 gap-2">
            {/* Logo & Company Title */}
            <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
              {companyInfo.logoUrl ? (
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white flex items-center justify-center p-1 shadow-md shadow-blue-500/10 overflow-hidden shrink-0 border border-slate-700">
                  <img
                    src={companyInfo.logoUrl}
                    alt={companyInfo.name}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ) : (
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-gradient-to-tr from-blue-600 to-indigo-500 flex items-center justify-center shadow-md shadow-blue-500/20 shrink-0">
                  <Wrench className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                </div>
              )}
              <div className="min-w-0">
                <h1 className="font-bold text-sm sm:text-lg leading-tight tracking-tight text-white truncate max-w-[140px] xs:max-w-[190px] sm:max-w-none">
                  {companyInfo.name}
                </h1>
                <p className="text-[11px] text-slate-400 hidden sm:block">
                  Teknik Servis, Müşteri Kaydı, Arıza Takip ve Faturalandırma
                </p>
                <div className="flex items-center gap-1.5 sm:hidden text-[10px] text-slate-400">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                  <span>{activeCount} Aktif Servis</span>
                </div>
              </div>
            </div>

            {/* Quick Metrics (Desktop only) */}
            <div className="hidden lg:flex items-center space-x-4 text-xs">
              <div className="bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-1.5 flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                <span className="text-slate-300">Aktif Servis:</span>
                <span className="font-bold text-amber-300 text-sm">{activeCount}</span>
              </div>
              <div className="bg-slate-800/80 border border-slate-700/60 rounded-lg px-3 py-1.5 flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-emerald-400" />
                <span className="text-slate-300">Teslimata Hazır:</span>
                <span className="font-bold text-emerald-300 text-sm">{readyCount}</span>
              </div>
            </div>

            {/* Primary Actions */}
            <div className="flex items-center space-x-1.5 sm:space-x-2 shrink-0">
              {/* Müşteri Cihaz Takip Portalı Linki */}
              <div className="flex items-center bg-slate-800/80 border border-slate-700/60 rounded-lg p-0.5">
                <a
                  href="/takip"
                  target="_blank"
                  rel="noreferrer"
                  title="Müşteri Cihaz Durum Takip Portalını Aç"
                  className="px-2 sm:px-2.5 py-1.5 text-xs font-semibold text-slate-300 hover:text-white hover:bg-slate-700/80 rounded-md flex items-center gap-1 transition-colors"
                >
                  <Smartphone className="w-3.5 h-3.5 text-blue-400" />
                  <span className="hidden sm:inline">Takip Portalı</span>
                  <ExternalLink className="w-3 h-3 text-slate-400 hidden xs:inline" />
                </a>
                <button
                  type="button"
                  onClick={handleCopyTrackingLink}
                  title="Müşterilere Gönderilecek Takip Linkini Kopyala"
                  className="p-1.5 text-slate-400 hover:text-blue-300 hover:bg-slate-700/80 rounded-md transition-colors cursor-pointer"
                >
                  {copiedTrackLink ? (
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              </div>

              <button
                onClick={onOpenNewTicket}
                className="bg-blue-600 hover:bg-blue-500 text-white font-semibold text-xs sm:text-sm px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-lg flex items-center space-x-1.5 shadow-md shadow-blue-600/30 transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer shrink-0"
              >
                <PlusCircle className="w-4 h-4 shrink-0" />
                <span className="hidden xs:inline">Yeni Kayıt</span>
                <span className="xs:hidden">Yeni</span>
              </button>

              {/* Firma ve Servis Ayarları */}
              {isAdmin && (
                <button
                  onClick={onOpenSettings}
                  title="Firma ve Servis Ayarları"
                  className="p-1.5 sm:p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded-lg transition-colors border border-slate-700/60 cursor-pointer"
                >
                  <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}

              {onLogout && (
                <button
                  onClick={onLogout}
                  title="Oturumu Kapat / Çıkış Yap"
                  className="p-1.5 sm:p-2 text-rose-300 hover:text-white hover:bg-rose-600/80 rounded-lg transition-colors border border-rose-500/30 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </div>

          {/* Desktop & Tablet Top Tab Navigation */}
          <div className="hidden md:flex space-x-1 border-t border-slate-800 pt-2 pb-1 overflow-x-auto no-scrollbar">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-4 py-2 rounded-md font-medium text-sm flex items-center space-x-2 transition-all cursor-pointer ${
                activeTab === 'dashboard'
                  ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>Servis Paneli & Takip</span>
              {activeCount > 0 && (
                <span className="ml-1.5 bg-blue-500 text-white text-xs px-1.5 py-0.2 rounded-full font-bold">
                  {activeCount}
                </span>
              )}
            </button>

            <button
              onClick={() => setActiveTab('history')}
              className={`px-4 py-2 rounded-md font-medium text-sm flex items-center space-x-2 transition-all cursor-pointer ${
                activeTab === 'history'
                  ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <Users className="w-4 h-4" />
              <span>Müşteri ve Cariler</span>
            </button>

            {isAdmin && (
              <button
                onClick={() => setActiveTab('archive')}
                className={`px-4 py-2 rounded-md font-medium text-sm flex items-center space-x-2 transition-all cursor-pointer ${
                  activeTab === 'archive'
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-400/30'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`}
              >
                <Archive className="w-4 h-4" />
                <span>Gizli Form Arşivi</span>
              </button>
            )}

            {!isLimitedTechnician && (
              <button
                onClick={() => setActiveTab('inventory')}
                className={`px-4 py-2 rounded-md font-medium text-sm flex items-center space-x-2 transition-all cursor-pointer ${
                  activeTab === 'inventory'
                    ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                    : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                }`}
              >
                <Package className="w-4 h-4" />
                <span>Stok & Yedek Parça</span>
                {criticalStockCount > 0 && (
                  <span className="ml-1.5 bg-amber-500 text-slate-900 font-bold text-[10px] px-1.5 py-0.2 rounded-full animate-pulse flex items-center gap-0.5">
                    <AlertTriangle className="w-2.5 h-2.5" />
                    {criticalStockCount}
                  </span>
                )}
              </button>
            )}

            <button
              onClick={() => setActiveTab('reports')}
              className={`px-4 py-2 rounded-md font-medium text-sm flex items-center space-x-2 transition-all cursor-pointer ${
                activeTab === 'reports'
                  ? 'bg-blue-600/20 text-blue-300 border border-blue-500/30'
                  : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
              }`}
            >
              <TrendingUp className="w-4 h-4" />
              <span>{isLimitedTechnician ? 'Günlük ve Aylık Raporlar' : 'Aylık Kazanç & Raporlar'}</span>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Bottom Navigation Bar (Thumb Friendly on iOS & Android) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-slate-900/95 backdrop-blur-lg border-t border-slate-800 px-2 py-1 pb-safe flex items-center justify-around shadow-2xl">
        {/* 1. Servis Paneli */}
        <button
          type="button"
          onClick={() => setActiveTab('dashboard')}
          className={`flex-1 py-1.5 px-1 flex flex-col items-center justify-center rounded-xl transition-all relative ${
            activeTab === 'dashboard'
              ? 'text-blue-400 font-bold bg-blue-500/10'
              : 'text-slate-400 hover:text-slate-200 font-medium'
          }`}
        >
          <div className="relative">
            <LayoutDashboard className="w-5 h-5" />
            {activeCount > 0 && (
              <span className="absolute -top-1 -right-2.5 bg-blue-500 text-white font-bold text-[9px] min-w-[16px] h-4 rounded-full flex items-center justify-center px-1">
                {activeCount > 99 ? '99+' : activeCount}
              </span>
            )}
          </div>
          <span className="text-[10px] mt-0.5 truncate max-w-full">Servisler</span>
        </button>

        {/* 2. Müşteri & Cariler */}
        <button
          type="button"
          onClick={() => setActiveTab('history')}
          className={`flex-1 py-1.5 px-1 flex flex-col items-center justify-center rounded-xl transition-all ${
            activeTab === 'history'
              ? 'text-blue-400 font-bold bg-blue-500/10'
              : 'text-slate-400 hover:text-slate-200 font-medium'
          }`}
        >
          <Users className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 truncate max-w-full">Müşteriler</span>
        </button>

        {isAdmin && (
          <button
            type="button"
            onClick={() => setActiveTab('archive')}
            className={`flex-1 py-1.5 px-1 flex flex-col items-center justify-center rounded-xl transition-all ${
              activeTab === 'archive'
                ? 'text-amber-400 font-bold bg-amber-500/10'
                : 'text-slate-400 hover:text-slate-200 font-medium'
            }`}
          >
            <Archive className="w-5 h-5" />
            <span className="text-[10px] mt-0.5 truncate max-w-full">Arşiv</span>
          </button>
        )}

        {/* Center Quick + Add button */}
        <button
          type="button"
          onClick={onOpenNewTicket}
          className="w-11 h-11 -mt-5 rounded-full bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-600/40 flex items-center justify-center border-2 border-slate-900 active:scale-95 transition-all cursor-pointer shrink-0"
          title="Yeni Servis Kaydı Aç"
        >
          <PlusCircle className="w-6 h-6" />
        </button>

        {/* 3. Stok & Yedek Parça */}
        {!isLimitedTechnician && (
          <button
            type="button"
            onClick={() => setActiveTab('inventory')}
            className={`flex-1 py-1.5 px-1 flex flex-col items-center justify-center rounded-xl transition-all relative ${
              activeTab === 'inventory'
                ? 'text-blue-400 font-bold bg-blue-500/10'
                : 'text-slate-400 hover:text-slate-200 font-medium'
            }`}
          >
            <div className="relative">
              <Package className="w-5 h-5" />
              {criticalStockCount > 0 && (
                <span className="absolute -top-1 -right-2 bg-amber-500 text-slate-950 font-extrabold text-[9px] w-3.5 h-3.5 rounded-full flex items-center justify-center animate-pulse">
                  !
                </span>
              )}
            </div>
            <span className="text-[10px] mt-0.5 truncate max-w-full">Stok</span>
          </button>
        )}

        {/* 4. Raporlar */}
        <button
          type="button"
          onClick={() => setActiveTab('reports')}
          className={`flex-1 py-1.5 px-1 flex flex-col items-center justify-center rounded-xl transition-all ${
            activeTab === 'reports'
              ? 'text-blue-400 font-bold bg-blue-500/10'
              : 'text-slate-400 hover:text-slate-200 font-medium'
          }`}
        >
          <TrendingUp className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 truncate max-w-full">Raporlar</span>
        </button>
      </nav>
    </>
  );
};
